import { useLazyQuery } from '@apollo/client';
import { SEND_TALENTS_REPORT } from 'graphql/user';
import { useCurrentUser } from 'hooks/auth';
import { useAuth0 } from 'hooks/auth/useAuth0';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import React, { useCallback } from 'react';

import { Description } from '@mui/icons-material';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Button,
  Grid,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import { CurrencyEnum, Query } from '@libs/graphql-types';
import { formatRate } from '@libs/ui/components/job/utils/common';

interface WorkforceData {
  pendingVerification: number;
  totalInvitesSent: number;
  totalActiveAccounts: number;
  averageHourlyRate: number;
}

interface MyWorkforceProps {
  data?: WorkforceData;
  isLoading?: boolean;
  totalTalents?: number;
  pendingTalents?: number;
  staticTalents?: number;
  generalAnalytics?: any[];
}

const useStyles = makeStyles((theme) => ({
  button: {
    height: '30px',
    padding: '0 8px',
    marginLeft: 'auto',
  },
  gridItem: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  value: {
    fontWeight: 500,
  },
  blueValue: {
    color: theme.palette.info.main,
    fontWeight: 500,
  },
}));

const MyWorkforce: React.FC<MyWorkforceProps> = ({
  totalTalents,
  pendingTalents,
  staticTalents,
  generalAnalytics = [],
}) => {
  const classes = useStyles();
  const confirm = useConfirm();
  const { getData } = useCurrentUser();
  const currency =
    getData()?.data?.currentCompanyUser?.company?.currency || CurrencyEnum.Usd;
  const {
    user: { email },
  } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();
  const [getReport] = useLazyQuery<Query>(SEND_TALENTS_REPORT, {
    onCompleted: () => {
      enqueueSnackbar(`Report was sent to ${email}`, {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar(`Report was not sent`, {
        variant: 'error',
      });
    },
  });

  const onSend = useCallback(async () => {
    await confirm({
      title: `Send report to ${email}`,
      confirmationButtonProps: { autoFocus: true },
      confirmationText: 'Send',
    });
    getReport();
  }, [confirm, email, getReport]);

  const formatCurrency = (value: number) => {
    return formatRate({
      min: value,
      currency,
    });
  };

  return (
    <Card elevation={0}>
      <CardHeader
        title={<Typography variant="h6">My Workforce</Typography>}
        action={
          <Button
            variant="outlined"
            color="info"
            onClick={onSend}
            endIcon={<Description />}
            className={classes.button}
          >
            Report
          </Button>
        }
      />
      <CardContent>
        <Box>
          <Grid spacing={2} container direction="column">
            <Grid justifyContent="space-between" container item>
              <Grid item>Pending Verification</Grid>
              <Grid item>
                {pendingTalents
                  ? new Intl.NumberFormat().format(pendingTalents)
                  : '-'}
              </Grid>
            </Grid>
            {generalAnalytics
              .filter((item) => item.key !== 'Invites sent')
              .map((item) => (
                <Grid
                  key={item?.key}
                  justifyContent="space-between"
                  container
                  item
                >
                  <Grid item>{item?.key}</Grid>
                  <Grid item>
                    {item.slug === 'average_rate'
                      ? item.value !== undefined
                        ? formatCurrency(Math.floor(item.value))
                        : '-'
                      : item.value !== undefined
                      ? new Intl.NumberFormat().format(item.value)
                      : '-'}
                  </Grid>
                </Grid>
              ))}
            <Grid justifyContent="space-between" container item>
              <Grid item>Static Accounts</Grid>
              <Grid item>
                {staticTalents !== undefined
                  ? new Intl.NumberFormat().format(staticTalents)
                  : '-'}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MyWorkforce;
